// The one entry point for pages: areas in, shapes and a map view out.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { areaCountry } from '../../config/countries';
import type { MapView } from '../../config/countries';
import { areaShapeLoader } from './defaultLoader';
import { fitView } from './fitView';
import type { FitOptions } from './fitView';
import type { AreaShapeLoader } from './loader';
import type { AreaCell, AreaLike, AreaShape } from './types';

export type AreaShapesStatus = 'loading' | 'ready' | 'failed';

export interface UseAreaShapesOptions {
  /** Hold off until true (a thumbnail waits until it is near the screen). Default true. */
  enabled?: boolean;
  /** Ask for at most this many ids per area. */
  maxIds?: number;
  /** The size of the map, for the zoom. */
  fit?: FitOptions;
  /** Tests only: a loader over a fake maps API. */
  loader?: AreaShapeLoader;
}

export interface AreaShapesResult {
  /** The outlines, normalised to GeoJSON; [] until loaded and when failed. */
  shapes: AreaShape[];
  /** The same shapes with the id each one was asked for. */
  cells: AreaCell[];
  /**
   * Where a map showing the areas opens: on the shapes when there are any, otherwise the default view of
   * the areas' country (while loading, after a failure, or when nothing can be drawn).
   */
  view: MapView;
  status: AreaShapesStatus;
  /** Why it failed, when status is 'failed' (for the message); otherwise null. */
  error: unknown;
  /** Ask again after a failure. */
  retry: () => void;
}

const NO_CELLS: AreaCell[] = [];

/** The country a set of areas belongs to: the first that says (or can be told). */
function countryOf(areas: readonly AreaLike[]) {
  for (const area of areas) {
    const code = areaCountry(area);
    if (code) return code;
  }
  return null;
}

export function useAreaShapes(areas: readonly AreaLike[], options: UseAreaShapesOptions = {}): AreaShapesResult {
  const { enabled = true, maxIds, fit, loader = areaShapeLoader } = options;
  const [loaded, setLoaded] = useState<{ key: string; cells: AreaCell[] } | { key: string; failed: true; error: unknown } | null>(null);
  const [attempt, setAttempt] = useState(0);

  const key = areas.map((a) => `${a.id}|${a.type}|${a.country_code ?? ''}|${a.ids?.join(',') ?? ''}`).join('\n') + `#${attempt}`;
  const areasRef = useRef(areas);
  areasRef.current = areas;

  useEffect(() => {
    if (!enabled) return;
    let live = true;
    Promise.all(areasRef.current.map((a) => loader.load(a, { maxIds }))).then(
      (all) => live && setLoaded({ key, cells: all.flat() }),
      (error) => live && setLoaded({ key, failed: true, error }),
    );
    return () => {
      live = false;
    };
  }, [enabled, key, maxIds, loader]);

  const current = loaded && loaded.key === key ? loaded : null;
  const cells = current && 'cells' in current ? current.cells : NO_CELLS;
  const status: AreaShapesStatus = !current ? 'loading' : 'cells' in current ? 'ready' : 'failed';
  const shapes = useMemo(() => cells.map((c) => c.geometry), [cells]);
  const country = countryOf(areas);
  const fitKey = fit ? `${fit.width}x${fit.height}:${fit.minZoom}:${fit.maxZoom}` : '';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const view = useMemo(() => fitView(shapes, country, fit), [shapes, country, fitKey]);
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  const error = current && 'failed' in current ? current.error : null;

  return { shapes, cells, view, status, error, retry };
}
